// Filter dialog functions — prompt helpers for all destructive filter operations.
// Extracted from main.ts to reduce its line count.
//
// Each function follows the pattern:
//   1. Guard: if (!deps.state.doc) return
//   2. Open a FilterDialog with parameters
//   3. Call deps.applyFilter(FilterClass, params) on OK

import type { EditorState } from "./state/EditorState";
import type { FilterDialog } from "./ui/FilterDialog";
import type { Filter } from "../core/filters/Filter";
import { ApplyFilterCommand } from "../core/commands/ApplyFilterCommand";
import { GaussianBlur } from "../core/filters/GaussianBlur";
import { BoxBlur } from "../core/filters/BoxBlur";
import { MotionBlur } from "../core/filters/MotionBlur";
import { AverageBlur } from "../core/filters/AverageBlur";
import { SurfaceBlur } from "../core/filters/SurfaceBlur";
import { SmartBlur } from "../core/filters/SmartBlur";
import { LensBlur } from "../core/filters/LensBlur";
import { RadialBlur } from "../core/filters/RadialBlur";
import { ShapeBlur } from "../core/filters/ShapeBlur";
import { FieldBlur } from "../core/filters/FieldBlur";
import { IrisBlur } from "../core/filters/IrisBlur";
import { TiltShift } from "../core/filters/TiltShift";
import { SharpenSuite } from "../core/filters/SharpenSuite";
import { SmartSharpen } from "../core/filters/SmartSharpen";
import { NoiseSuite } from "../core/filters/NoiseSuite";
import { PixelateSuite } from "../core/filters/PixelateSuite";
import { DistortSuite } from "../core/filters/DistortSuite";
import { StylizeSuite } from "../core/filters/StylizeSuite";
import { UtilitySuite } from "../core/filters/UtilitySuite";
import { Displace } from "../core/filters/Displace";
import { Ripple } from "../core/filters/Ripple";
import { Shear } from "../core/filters/Shear";
import { Wave } from "../core/filters/Wave";
import { ZigZag } from "../core/filters/ZigZag";
import { PolarCoordinates } from "../core/filters/PolarCoordinates";
import { Crystallize } from "../core/filters/Crystallize";
import { Facet } from "../core/filters/Facet";
import { Fragment } from "../core/filters/Fragment";
import { Mezzotint } from "../core/filters/Mezzotint";
import { Pointillize } from "../core/filters/Pointillize";
import { Diffuse } from "../core/filters/Diffuse";
import { Extrude } from "../core/filters/Extrude";
import { Solarize } from "../core/filters/Solarize";
import { Tiles } from "../core/filters/Tiles";
import { TraceContour } from "../core/filters/TraceContour";
import { Wind } from "../core/filters/Wind";
import { GlowingEdges } from "../core/filters/GlowingEdges";
import { Fibers } from "../core/filters/Fibers";
import { LensFlare } from "../core/filters/LensFlare";
import { LightingEffects } from "../core/filters/LightingEffects";
import { PictureFrame } from "../core/filters/PictureFrame";
import { OilPaint } from "../core/filters/OilPaint";
import { BrightnessContrast } from "../core/filters/BrightnessContrast";
import { ShadowsHighlights } from "../core/filters/ShadowsHighlights";
import { Exposure } from "../core/filters/Exposure";
import { PhotoFilter } from "../core/filters/PhotoFilter";
import { ChannelMixer } from "../core/filters/ChannelMixer";
import { Invert } from "../core/filters/Invert";
import { Posterize } from "../core/filters/Posterize";
import { Threshold } from "../core/filters/Threshold";
import { GradientMap } from "../core/filters/GradientMap";
import { ColorLookup } from "../core/filters/ColorLookup";
import { SelectiveColor } from "../core/filters/SelectiveColor";
import { AutoTone } from "../core/filters/AutoTone";
import { AutoContrast } from "../core/filters/AutoContrast";
import { AutoColor } from "../core/filters/AutoColor";
import { CameraRaw } from "../core/filters/CameraRaw";
import { Liquify } from "../core/filters/Liquify";

export interface FilterDialogDeps {
  state: EditorState;
  filterDialog: FilterDialog;
  showTip: (msg: string) => void;
  /** Stored last filter for Ctrl+F repeat. Modified by applyFilter. */
  lastFilter: { filter: Filter<unknown>; params: unknown } | null;
  /** Callback to notify main that lastFilter changed. */
  onLastFilterChange: (f: { filter: Filter<unknown>; params: unknown } | null) => void;
}

// ---------------------------------------------------------------------------
// Core helpers (shared by all prompt functions)
// ---------------------------------------------------------------------------

export function applyFilter<P>(deps: FilterDialogDeps, filter: Filter<P>, params: P): void {
  const doc = deps.state.doc;
  if (!doc) return;
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  deps.state.execute(new ApplyFilterCommand(id, filter, params, doc.selection ?? null));
  deps.onLastFilterChange({ filter: filter as Filter<unknown>, params });
}

type FieldDef = {
  key: string;
  label: string;
  type: "slider" | "dropdown" | "checkbox" | "color";
  default: number | string | boolean;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: Array<{ value: string; label: string }>;
};

export async function openFilterDialog(
  deps: FilterDialogDeps,
  title: string,
  params: FieldDef[],
  apply: (r: Record<string, number | boolean | string>) => void,
): Promise<void> {
  const result = await deps.filterDialog.open(title, params);
  if (!result) return;
  apply(result as Record<string, number | boolean | string>);
}

// ---------------------------------------------------------------------------
// Blur filters
// ---------------------------------------------------------------------------

export async function promptGaussianBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Gaussian Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 5, min: 0.1, max: 250, step: 0.1, unit: "px" },
  ], (r) => { applyFilter(deps, GaussianBlur, { radius: r.radius as number }); });
}

export async function promptBoxBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Box Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 5, min: 1, max: 100, unit: "px" },
  ], (r) => { applyFilter(deps, BoxBlur, { radius: r.radius as number }); });
}

export async function promptMotionBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Motion Blur", [
    { key: "angle", label: "Angle", type: "slider", default: 0, min: -180, max: 180, unit: "°" },
    { key: "distance", label: "Distance", type: "slider", default: 20, min: 1, max: 999, unit: "px" },
  ], (r) => { applyFilter(deps, MotionBlur, { angle: r.angle as number, distance: r.distance as number }); });
}

export async function promptAverageBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Average Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 5, min: 1, max: 50, unit: "px" },
  ], (r) => { applyFilter(deps, AverageBlur, { radius: r.radius as number }); });
}

export async function promptSurfaceBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Surface Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 5, min: 1, max: 50, unit: "px" },
    { key: "threshold", label: "Threshold", type: "slider", default: 15, min: 0, max: 255 },
  ], (r) => { applyFilter(deps, SurfaceBlur, { radius: r.radius as number, threshold: r.threshold as number }); });
}

export async function promptSmartBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Smart Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 5, min: 1, max: 50, unit: "px" },
    { key: "threshold", label: "Threshold", type: "slider", default: 25, min: 0, max: 255 },
    { key: "quality", label: "Quality", type: "slider", default: 3, min: 1, max: 10 },
    { key: "mode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "edge-only", label: "Edge Only" },
      { value: "overlay", label: "Overlay" },
    ]},
  ], (r) => { applyFilter(deps, SmartBlur, { radius: r.radius as number, threshold: r.threshold as number, quality: r.quality as number, mode: r.mode as any }); });
}

export async function promptLensBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Lens Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 10, min: 1, max: 50, unit: "px" },
    { key: "bladeCurvature", label: "Blade Curvature", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
    { key: "rotation", label: "Rotation", type: "slider", default: 0, min: 0, max: 360, unit: "°" },
  ], (r) => { applyFilter(deps, LensBlur, { radius: r.radius as number, bladeCurvature: r.bladeCurvature as number, rotation: r.rotation as number }); });
}

export async function promptRadialBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Radial Blur", [
    { key: "amount", label: "Amount", type: "slider", default: 10, min: 1, max: 100 },
    { key: "mode", label: "Method", type: "dropdown", default: "zoom", options: [
      { value: "zoom", label: "Zoom" },
      { value: "spin", label: "Spin" },
    ]},
  ], (r) => { applyFilter(deps, RadialBlur, { amount: r.amount as number, mode: r.mode as any }); });
}

export async function promptShapeBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Shape Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 10, min: 1, max: 50, unit: "px" },
    { key: "shape", label: "Shape", type: "dropdown", default: "circle", options: [
      { value: "circle", label: "Circle" },
      { value: "square", label: "Square" },
      { value: "triangle", label: "Triangle" },
      { value: "star", label: "Star" },
    ]},
  ], (r) => { applyFilter(deps, ShapeBlur, { radius: r.radius as number, shape: r.shape as any }); });
}

export async function promptFieldBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Field Blur", [
    { key: "blur", label: "Blur", type: "slider", default: 10, min: 0, max: 50, unit: "px" },
  ], (r) => { applyFilter(deps, FieldBlur, { points: [{ x: 0.5, y: 0.5, blur: r.blur as number }] }); });
}

export async function promptIrisBlur(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Iris Blur", [
    { key: "blur", label: "Blur", type: "slider", default: 15, min: 0, max: 50, unit: "px" },
    { key: "irisSizeX", label: "Iris Width", type: "slider", default: 30, min: 5, max: 80, unit: "%" },
    { key: "irisSizeY", label: "Iris Height", type: "slider", default: 30, min: 5, max: 80, unit: "%" },
  ], (r) => { applyFilter(deps, IrisBlur, { blur: r.blur as number, irisShape: 6, irisRoundness: 50, irisCenterX: 50, irisCenterY: 50, irisSizeX: r.irisSizeX as number, irisSizeY: r.irisSizeY as number }); });
}

export async function promptTiltShift(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Tilt Shift", [
    { key: "blur", label: "Blur", type: "slider", default: 15, min: 0, max: 50, unit: "px" },
    { key: "inFocusBottom", label: "In-Focus Bottom", type: "slider", default: 40, min: 0, max: 100, unit: "%" },
    { key: "inFocusTop", label: "In-Focus Top", type: "slider", default: 60, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, TiltShift, { blur: r.blur as number, inFocusBottom: r.inFocusBottom as number, inFocusTop: r.inFocusTop as number, distortion: 0 }); });
}

// ---------------------------------------------------------------------------
// Sharpen filters
// ---------------------------------------------------------------------------

export function promptSharpen(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, SharpenSuite, { mode: "sharpen", amount: 100, radius: 1, threshold: 0 });
}

export function promptSharpenMore(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, SharpenSuite, { mode: "sharpen-more", amount: 100, radius: 1, threshold: 0 });
}

export async function promptUnsharpMask(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Unsharp Mask", [
    { key: "amount", label: "Amount", type: "slider", default: 100, min: 0, max: 500, unit: "%" },
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 0.1, max: 250, step: 0.1, unit: "px" },
    { key: "threshold", label: "Threshold", type: "slider", default: 0, min: 0, max: 255 },
  ], (r) => {
    applyFilter(deps, SharpenSuite, { mode: "unsharp-mask", amount: r.amount as number, radius: r.radius as number, threshold: r.threshold as number });
  });
}

export async function promptSmartSharpen(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Smart Sharpen", [
    { key: "amount", label: "Amount", type: "slider", default: 150, min: 0, max: 500, unit: "%" },
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 0.1, max: 250, step: 0.1, unit: "px" },
    { key: "reduce", label: "Reduce Noise", type: "slider", default: 0, min: 0, max: 100, unit: "%" },
    { key: "remove", label: "Remove", type: "dropdown", default: "gaussian", options: [
      { value: "gaussian", label: "Gaussian Blur" },
      { value: "lens", label: "Lens Blur" },
      { value: "motion", label: "Motion Blur" },
    ]},
  ], (r) => {
    applyFilter(deps, SharpenSuite, { mode: "unsharp-mask", amount: r.amount as number, radius: r.radius as number, threshold: r.reduce as number });
  });
}

export function promptSharpenEdges(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, SharpenSuite, { mode: "sharpen-edges", amount: 100, radius: 1, threshold: 0 });
}

// ---------------------------------------------------------------------------
// Noise filters
// ---------------------------------------------------------------------------

export async function promptAddNoise(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Add Noise", [
    { key: "amount", label: "Amount", type: "slider", default: 10, min: 0, max: 100, unit: "%" },
    { key: "distribution", label: "Distribution", type: "dropdown", default: "uniform", options: [
      { value: "uniform", label: "Uniform" },
      { value: "gaussian", label: "Gaussian" },
    ]},
    { key: "monochrome", label: "Monochromatic", type: "checkbox", default: false },
  ], (r) => {
    applyFilter(deps, NoiseSuite, { mode: "add-noise", amount: r.amount as number, distribution: r.distribution as any, monochrome: r.monochrome as boolean, radius: 1, threshold: 0, strength: 10, preserveDetails: true });
  });
}

export async function promptMedian(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Median", [
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 1, max: 50, unit: "px" },
  ], (r) => {
    applyFilter(deps, NoiseSuite, { mode: "median", amount: 0, distribution: "uniform", monochrome: false, radius: Math.round(r.radius as number), threshold: 0, strength: 10, preserveDetails: true });
  });
}

export async function promptDustScratches(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Dust & Scratches", [
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 1, max: 50, unit: "px" },
    { key: "threshold", label: "Threshold", type: "slider", default: 0, min: 0, max: 255 },
  ], (r) => {
    applyFilter(deps, NoiseSuite, { mode: "dust-scratches", amount: 0, distribution: "uniform", monochrome: false, radius: Math.round(r.radius as number), threshold: r.threshold as number, strength: 10, preserveDetails: true });
  });
}

export function promptDespeckle(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, NoiseSuite, { mode: "median", amount: 0, distribution: "uniform", monochrome: false, radius: 1, threshold: 0, strength: 10, preserveDetails: true });
}

export function promptReduceNoise(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, NoiseSuite, { mode: "reduce-noise", amount: 0, distribution: "uniform", monochrome: true, radius: 3, threshold: 10, strength: 10, preserveDetails: true });
}

// ---------------------------------------------------------------------------
// Pixelate filters
// ---------------------------------------------------------------------------

export async function promptMosaic(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Mosaic", [
    { key: "cellSize", label: "Cell Size", type: "slider", default: 8, min: 2, max: 200, unit: "px" },
  ], (r) => {
    applyFilter(deps, PixelateSuite, { mode: "mosaic", cellSize: r.cellSize as number, maxRadius: 0, screenAngles: [105, 75, 90, 45] });
  });
}

export async function promptCrystallize(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Crystallize", [
    { key: "cellSize", label: "Cell Size", type: "slider", default: 10, min: 2, max: 100, unit: "px" },
  ], (r) => { applyFilter(deps, Crystallize, { cellSize: r.cellSize as number }); });
}

export async function promptFacet(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Facet", [
    { key: "radius", label: "Radius", type: "slider", default: 8, min: 2, max: 50, unit: "px" },
  ], (r) => { applyFilter(deps, Facet, { radius: r.radius as number }); });
}

export function promptFragment(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, Fragment, { copies: 4, offset: 3 });
}

export async function promptMezzotint(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Mezzotint", [
    { key: "type", label: "Type", type: "dropdown", default: "fine-dots", options: [
      { value: "fine-dots", label: "Fine Dots" },
      { value: "coarse-dots", label: "Coarse Dots" },
      { value: "fine-lines", label: "Fine Lines" },
      { value: "coarse-lines", label: "Coarse Lines" },
      { value: "stipple", label: "Stipple" },
    ]},
  ], (r) => { applyFilter(deps, Mezzotint, { type: r.type as any }); });
}

export async function promptPointillize(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Pointillize", [
    { key: "dotSize", label: "Dot Size", type: "slider", default: 6, min: 2, max: 50, unit: "px" },
  ], (r) => { applyFilter(deps, Pointillize, { dotSize: r.dotSize as number }); });
}

// ---------------------------------------------------------------------------
// Distort filters
// ---------------------------------------------------------------------------

export async function promptTwirl(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Twirl", [
    { key: "amount", label: "Angle", type: "slider", default: 50, min: -360, max: 360, unit: "°" },
  ], (r) => {
    applyFilter(deps, DistortSuite, { mode: "twirl", amount: r.amount as number });
  });
}

export async function promptPinch(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Pinch", [
    { key: "amount", label: "Amount", type: "slider", default: 50, min: -100, max: 100 },
  ], (r) => {
    applyFilter(deps, DistortSuite, { mode: "pinch", amount: r.amount as number });
  });
}

export async function promptSpherize(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Spherize", [
    { key: "amount", label: "Amount", type: "slider", default: 50, min: -100, max: 100 },
  ], (r) => {
    applyFilter(deps, DistortSuite, { mode: "spherize", amount: r.amount as number });
  });
}

export async function promptDisplace(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Displace", [
    { key: "horizontalScale", label: "Horizontal Scale", type: "slider", default: 10, min: -100, max: 100, unit: "%" },
    { key: "verticalScale", label: "Vertical Scale", type: "slider", default: 10, min: -100, max: 100, unit: "%" },
    { key: "undefinedAreas", label: "Undefined Areas", type: "dropdown", default: "stretch", options: [
      { value: "stretch", label: "Stretch to Fit" },
      { value: "wrap", label: "Wrap Around" },
    ]},
  ], (r) => { applyFilter(deps, Displace, { horizontalScale: r.horizontalScale as number, verticalScale: r.verticalScale as number, undefinedAreas: r.undefinedAreas as any }); });
}

export async function promptRipple(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Ripple", [
    { key: "amount", label: "Amount", type: "slider", default: 100, min: 0, max: 999 },
    { key: "size", label: "Size", type: "dropdown", default: "10", options: [
      { value: "5", label: "Small" },
      { value: "10", label: "Medium" },
      { value: "20", label: "Large" },
    ]},
  ], (r) => { applyFilter(deps, Ripple, { amount: r.amount as number, size: parseInt(String(r.size)) }); });
}

export async function promptShear(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Shear", [
    { key: "undefinedAreas", label: "Undefined Areas", type: "dropdown", default: "wrap", options: [
      { value: "wrap", label: "Wrap Around" },
      { value: "repeat", label: "Repeat Edge Pixels" },
    ]},
  ], (r) => { applyFilter(deps, Shear, { points: [{ x: 0, y: 0 }, { x: 128, y: 50 }, { x: 255, y: 0 }], undefinedAreas: r.undefinedAreas as any }); });
}

export async function promptWave(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Wave", [
    { key: "numGenerators", label: "Number of Generators", type: "slider", default: 3, min: 1, max: 20 },
    { key: "wavelength", label: "Wavelength", type: "slider", default: 50, min: 1, max: 200 },
    { key: "amplitude", label: "Amplitude", type: "slider", default: 20, min: 0, max: 100 },
  ], (r) => { applyFilter(deps, Wave, { numGenerators: r.numGenerators as number, wavelength: r.wavelength as number, amplitude: r.amplitude as number, scale: 100 }); });
}

export async function promptZigZag(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "ZigZag", [
    { key: "amount", label: "Amount", type: "slider", default: 10, min: -100, max: 100 },
    { key: "ridges", label: "Ridges", type: "slider", default: 10, min: 1, max: 50 },
  ], (r) => { applyFilter(deps, ZigZag, { amount: r.amount as number, ridges: r.ridges as number }); });
}

export async function promptPolarCoordinates(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Polar Coordinates", [
    { key: "mode", label: "Conversion", type: "dropdown", default: "rect-to-polar", options: [
      { value: "rect-to-polar", label: "Rectangular to Polar" },
      { value: "polar-to-rect", label: "Polar to Rectangular" },
    ]},
  ], (r) => { applyFilter(deps, PolarCoordinates, { mode: r.mode as any }); });
}

// ---------------------------------------------------------------------------
// Stylize filters
// ---------------------------------------------------------------------------

export function promptFindEdges(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, StylizeSuite, { mode: "find-edges", angle: 135, height: 3, amount: 100 });
}

export async function promptEmboss(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Emboss", [
    { key: "angle", label: "Angle", type: "slider", default: 135, min: 0, max: 360, unit: "°" },
    { key: "height", label: "Height", type: "slider", default: 3, min: 1, max: 10, unit: "px" },
    { key: "amount", label: "Amount", type: "slider", default: 100, min: 1, max: 500, unit: "%" },
  ], (r) => {
    applyFilter(deps, StylizeSuite, { mode: "emboss", angle: r.angle as number, height: r.height as number, amount: r.amount as number });
  });
}

export async function promptDiffuse(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Diffuse", [
    { key: "mode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "anisotropic", label: "Anisotropic" },
    ]},
    { key: "intensity", label: "Intensity", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, Diffuse, { mode: r.mode as any, intensity: r.intensity as number }); });
}

export async function promptExtrude(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Extrude", [
    { key: "type", label: "Type", type: "dropdown", default: "block", options: [
      { value: "block", label: "Blocks" },
      { value: "pyramid", label: "Pyramids" },
    ]},
    { key: "size", label: "Size", type: "slider", default: 16, min: 2, max: 100, unit: "px" },
    { key: "depth", label: "Depth", type: "slider", default: 4, min: 1, max: 10 },
  ], (r) => { applyFilter(deps, Extrude, { type: r.type as any, size: r.size as number, depth: r.depth as number, random: false }); });
}

export async function promptSolarize(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Solarize", [
    { key: "threshold", label: "Threshold", type: "slider", default: 128, min: 0, max: 255 },
  ], (r) => { applyFilter(deps, Solarize, { threshold: r.threshold as number }); });
}

export async function promptTiles(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Tiles", [
    { key: "tileSize", label: "Tile Size", type: "slider", default: 16, min: 2, max: 100, unit: "px" },
    { key: "offset", label: "Offset", type: "slider", default: 25, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, Tiles, { tileSize: r.tileSize as number, offset: r.offset as number, fill: "wrap" }); });
}

export async function promptTraceContour(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Trace Contour", [
    { key: "level", label: "Level", type: "slider", default: 128, min: 0, max: 255 },
    { key: "direction", label: "Direction", type: "dropdown", default: "lighter", options: [
      { value: "lighter", label: "Lighter" },
      { value: "darker", label: "Darker" },
    ]},
  ], (r) => { applyFilter(deps, TraceContour, { level: r.level as number, direction: r.direction as any }); });
}

export async function promptWind(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Wind", [
    { key: "method", label: "Method", type: "dropdown", default: "wind", options: [
      { value: "wind", label: "Wind" },
      { value: "blast", label: "Blast" },
      { value: "staggered", label: "Staggered" },
    ]},
    { key: "direction", label: "Direction", type: "dropdown", default: "right", options: [
      { value: "left", label: "From the Left" },
      { value: "right", label: "From the Right" },
    ]},
    { key: "intensity", label: "Intensity", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, Wind, { method: r.method as any, direction: r.direction as any, intensity: r.intensity as number }); });
}

export async function promptGlowingEdges(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Glowing Edges", [
    { key: "edgeWidth", label: "Edge Width", type: "slider", default: 2, min: 1, max: 20, unit: "px" },
    { key: "edgeBrightness", label: "Edge Brightness", type: "slider", default: 15, min: 1, max: 50 },
    { key: "smoothness", label: "Smoothness", type: "slider", default: 4, min: 1, max: 20 },
  ], (r) => { applyFilter(deps, GlowingEdges, { edgeWidth: r.edgeWidth as number, edgeBrightness: r.edgeBrightness as number, smoothness: r.smoothness as number }); });
}

// ---------------------------------------------------------------------------
// Render filters
// ---------------------------------------------------------------------------

export function promptClouds(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, UtilitySuite, { mode: "clouds", radius: 10, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: Date.now() });
}

export function promptDiffClouds(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, UtilitySuite, { mode: "difference-clouds", radius: 10, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: Date.now() });
}

export async function promptFibers(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Fibers", [
    { key: "variance", label: "Variance", type: "slider", default: 16, min: 1, max: 100 },
    { key: "strength", label: "Strength", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, Fibers, { variance: r.variance as number, strength: r.strength as number }); });
}

export async function promptLensFlare(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Lens Flare", [
    { key: "brightness", label: "Brightness", type: "slider", default: 100, min: 10, max: 300, unit: "%" },
    { key: "lensType", label: "Lens Type", type: "dropdown", default: "1", options: [
      { value: "1", label: "50-300mm Zoom" },
      { value: "2", label: "35mm Prime" },
      { value: "3", label: "105mm Prime" },
      { value: "4", label: "Movie Prime" },
    ]},
  ], (r) => { applyFilter(deps, LensFlare, { brightness: r.brightness as number, lensType: parseInt(String(r.lensType)), positionX: 50, positionY: 50 }); });
}

export async function promptLightingEffects(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Lighting Effects", [
    { key: "lightType", label: "Light Type", type: "dropdown", default: "point", options: [
      { value: "point", label: "Point Light" },
      { value: "spot", label: "Spotlight" },
      { value: "directional", label: "Directional Light" },
    ]},
    { key: "intensity", label: "Intensity", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
    { key: "ambient", label: "Ambient", type: "slider", default: 20, min: 0, max: 100, unit: "%" },
  ], (r) => { applyFilter(deps, LightingEffects, { lightType: r.lightType as any, color: { r: 255, g: 255, b: 255 }, intensity: r.intensity as number, ambient: r.ambient as number, positionX: 50, positionY: 50, height: 50 }); });
}

export async function promptPictureFrame(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Picture Frame", [
    { key: "frameStyle", label: "Style", type: "slider", default: 1, min: 1, max: 3 },
    { key: "frameWidth", label: "Width", type: "slider", default: 20, min: 1, max: 100, unit: "px" },
  ], (r) => { applyFilter(deps, PictureFrame, { frameStyle: r.frameStyle as number, frameWidth: r.frameWidth as number, frameColor: { r: 139, g: 90, b: 43 } }); });
}

// ---------------------------------------------------------------------------
// Other/Utility filters
// ---------------------------------------------------------------------------

export async function promptHighPass(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "High Pass", [
    { key: "radius", label: "Radius", type: "slider", default: 10, min: 0.1, max: 250, step: 0.1, unit: "px" },
  ], (r) => {
    applyFilter(deps, UtilitySuite, { mode: "high-pass", radius: r.radius as number, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: 0 });
  });
}

export async function promptOffset(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  const bgColor = deps.state.bgColor;
  await openFilterDialog(deps, "Offset", [
    { key: "horizontal", label: "Horizontal", type: "slider", default: 0, min: -1000, max: 1000, unit: "px" },
    { key: "vertical", label: "Vertical", type: "slider", default: 0, min: -1000, max: 1000, unit: "px" },
    { key: "fill", label: "Undefined Areas", type: "dropdown", default: "wrap", options: [
      { value: "wrap", label: "Wrap Around" },
      { value: "edge", label: "Repeat Edge Pixels" },
      { value: "color", label: "Set to Background Color" },
    ]},
  ], (r) => {
    applyFilter(deps, UtilitySuite, { mode: "offset", radius: 0, offsetX: r.horizontal as number, offsetY: r.vertical as number, fill: r.fill as any, bgColor: { r: bgColor.r, g: bgColor.g, b: bgColor.b }, seed: 0 });
  });
}

export async function promptMaximum(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Maximum", [
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 1, max: 100, unit: "px" },
    { key: "preserve", label: "Preserve", type: "dropdown", default: "squareness", options: [
      { value: "squareness", label: "Squareness" },
      { value: "roundness", label: "Roundness" },
    ]},
  ], (r) => {
    applyFilter(deps, UtilitySuite, { mode: "maximum", radius: r.radius as number, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: 0 });
  });
}

export async function promptMinimum(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Minimum", [
    { key: "radius", label: "Radius", type: "slider", default: 1, min: 1, max: 100, unit: "px" },
  ], (r) => {
    applyFilter(deps, UtilitySuite, { mode: "minimum", radius: r.radius as number, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: 0 });
  });
}

export async function promptBrightnessContrast(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Brightness/Contrast", [
    { key: "brightness", label: "Brightness", type: "slider", default: 0, min: -150, max: 150 },
    { key: "contrast", label: "Contrast", type: "slider", default: 0, min: -150, max: 150 },
    { key: "useLegacy", label: "Use Legacy", type: "checkbox", default: false },
  ], (r) => {
    applyFilter(deps, BrightnessContrast, { brightness: r.brightness as number, contrast: r.contrast as number });
  });
}

export async function promptOilPaint(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Oil Paint", [
    { key: "stylization", label: "Stylization", type: "slider", default: 4, min: 0, max: 10 },
    { key: "cleanliness", label: "Cleanliness", type: "slider", default: 4, min: 0, max: 10 },
    { key: "scale", label: "Scale", type: "slider", default: 0.6, min: 0.1, max: 3, step: 0.1 },
    { key: "bristleDetail", label: "Bristle Detail", type: "slider", default: 3, min: 0, max: 10 },
  ], (r) => {
    applyFilter(deps, UtilitySuite, { mode: "oil-paint", radius: r.stylization as number, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: 0 });
    deps.showTip("Oil Paint applied.");
  });
}

// ---------------------------------------------------------------------------
// Adjustment filters (used destructively from Image menu)
// ---------------------------------------------------------------------------

export async function promptShadowsHighlights(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Shadows/Highlights", [
    { key: "shadowAmount", label: "Shadows", type: "slider", default: 35, min: 0, max: 100, unit: "%" },
    { key: "highlightAmount", label: "Highlights", type: "slider", default: 0, min: 0, max: 100, unit: "%" },
  ], (r) => {
    applyFilter(deps, ShadowsHighlights, {
      shadowAmount: r.shadowAmount as number, shadowTonalWidth: 50, shadowRadius: 30,
      highlightAmount: r.highlightAmount as number, highlightTonalWidth: 50, highlightRadius: 30,
      colorCorrection: 20, blackClip: 0, whiteClip: 0,
    });
  });
}

export async function promptExposure(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Exposure", [
    { key: "exposure", label: "Exposure", type: "slider", default: 0, min: -20, max: 20, step: 0.1, unit: "EV" },
    { key: "offset", label: "Offset", type: "slider", default: 0, min: -0.5, max: 0.5, step: 0.01 },
    { key: "gamma", label: "Gamma", type: "slider", default: 1, min: 0.01, max: 9.99, step: 0.01 },
  ], (r) => {
    const g = r.gamma as number;
    if (g <= 0) return;
    applyFilter(deps, Exposure, { exposure: r.exposure as number, offset: r.offset as number, gamma: g });
  });
}

export async function promptPhotoFilter(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Photo Filter", [
    { key: "density", label: "Density", type: "slider", default: 25, min: 0, max: 100, unit: "%" },
    { key: "preserveLuminosity", label: "Preserve Luminosity", type: "checkbox", default: true },
  ], (r) => {
    applyFilter(deps, PhotoFilter, {
      color: { r: 255, g: 180, b: 50 },
      density: r.density as number,
      preserveLuminosity: r.preserveLuminosity as boolean,
    });
  });
}

export function promptChannelMixer(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, ChannelMixer, {
    red: { red: 100, green: 0, blue: 0, constant: 0 },
    green: { red: 0, green: 100, blue: 0, constant: 0 },
    blue: { red: 0, blue: 100, green: 0, constant: 0 },
    monochrome: false,
  });
}

export function promptInvert(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, Invert, undefined);
}

export async function promptPosterize(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Posterize", [
    { key: "levels", label: "Levels", type: "slider", default: 4, min: 2, max: 255 },
  ], (r) => {
    applyFilter(deps, Posterize, { levels: Math.round(r.levels as number) });
  });
}

export async function promptThreshold(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Threshold", [
    { key: "level", label: "Threshold Level", type: "slider", default: 128, min: 1, max: 255 },
  ], (r) => {
    applyFilter(deps, Threshold, { level: Math.round(r.level as number) });
  });
}

export function promptGradientMap(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, GradientMap, {
    stops: [
      { position: 0, color: { r: 0, g: 0, b: 0 } },
      { position: 1, color: { r: 255, g: 255, b: 255 } },
    ],
    reverse: false,
    dither: false,
  });
}

export function promptColorLookup(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, ColorLookup, { lut: null, opacity: 1 });
}

export function promptSelectiveColor(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  const zero = { cyan: 0, magenta: 0, yellow: 0, black: 0 };
  applyFilter(deps, SelectiveColor, {
    reds: zero, yellows: zero, greens: zero, cyans: zero,
    blues: zero, magentas: zero, whites: zero, neutrals: zero, blacks: zero,
    method: "relative",
  });
}

export function promptAutoTone(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, AutoTone, { clip: 0.001 });
}

export function promptAutoContrast(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, AutoContrast, { clip: 0.001 });
}

export function promptAutoColor(deps: FilterDialogDeps): void {
  if (!deps.state.doc) return;
  applyFilter(deps, AutoColor, { clip: 0.001, neutralize: 0.5 });
}

// ---------------------------------------------------------------------------
// Camera Raw Filter (Shift+Ctrl+A)
// Uses the CameraRaw filter with a full parameter dialog.
// ---------------------------------------------------------------------------

export async function promptCameraRawFilter(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Camera Raw Filter", [
    { key: "exposure", label: "Exposure", type: "slider", default: 0, min: -5, max: 5, step: 0.05, unit: "EV" },
    { key: "contrast", label: "Contrast", type: "slider", default: 0, min: -100, max: 100 },
    { key: "highlights", label: "Highlights", type: "slider", default: 0, min: -100, max: 100 },
    { key: "shadows", label: "Shadows", type: "slider", default: 0, min: -100, max: 100 },
    { key: "whites", label: "Whites", type: "slider", default: 0, min: -100, max: 100 },
    { key: "blacks", label: "Blacks", type: "slider", default: 0, min: -100, max: 100 },
    { key: "clarity", label: "Clarity", type: "slider", default: 0, min: -100, max: 100 },
    { key: "vibrance", label: "Vibrance", type: "slider", default: 0, min: -100, max: 100 },
    { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
    { key: "temperature", label: "Temperature", type: "slider", default: 0, min: -100, max: 100 },
    { key: "tint", label: "Tint", type: "slider", default: 0, min: -100, max: 100 },
    { key: "sharpenAmount", label: "Sharpen Amount", type: "slider", default: 25, min: 0, max: 150 },
    { key: "noiseReduction", label: "Noise Reduction", type: "slider", default: 0, min: 0, max: 100 },
  ], (r) => {
    applyFilter(deps, CameraRaw, {
      exposure: r.exposure as number,
      contrast: r.contrast as number,
      highlights: r.highlights as number,
      shadows: r.shadows as number,
      whites: r.whites as number,
      blacks: r.blacks as number,
      clarity: r.clarity as number,
      vibrance: r.vibrance as number,
      saturation: r.saturation as number,
      temperature: r.temperature as number,
      tint: r.tint as number,
      sharpenAmount: r.sharpenAmount as number,
      sharpenRadius: 1,
      noiseReduction: r.noiseReduction as number,
    });
  });
}

// ---------------------------------------------------------------------------
// Liquify (Shift+Ctrl+X) — simplified version using forward warp
// ---------------------------------------------------------------------------

export async function promptLiquify(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "Liquify", [
    { key: "mode", label: "Tool", type: "dropdown", default: "push", options: [
      { value: "push", label: "Forward Warp" },
      { value: "twirl-clockwise", label: "Twirl Clockwise" },
      { value: "twirl-counter", label: "Twirl Counter-Clockwise" },
      { value: "pinch", label: "Pucker" },
      { value: "bloat", label: "Bloat" },
    ]},
    { key: "brushSize", label: "Brush Size", type: "slider", default: 100, min: 10, max: 500, unit: "px" },
    { key: "brushPressure", label: "Brush Pressure", type: "slider", default: 50, min: 0, max: 100 },
    { key: "brushDensity", label: "Brush Density", type: "slider", default: 50, min: 0, max: 100 },
  ], (r) => {
    // Apply a single centered brush stroke as a preview/demonstration
    const doc = deps.state.doc!;
    const centerX = doc.width / 2;
    const centerY = doc.height / 2;
    applyFilter(deps, Liquify, {
      mode: r.mode as any,
      brushSize: r.brushSize as number,
      brushPressure: r.brushPressure as number,
      brushDensity: r.brushDensity as number,
      brushRate: 50,
      strokes: [[{ x: centerX, y: centerY, radius: r.brushSize as number, pressure: (r.brushPressure as number) / 100 }]],
      freezeMask: null,
    });
  });
}

// ---------------------------------------------------------------------------
// HSBHSL utility filter
// ---------------------------------------------------------------------------

export async function promptHSBHSL(deps: FilterDialogDeps): Promise<void> {
  if (!deps.state.doc) return;
  await openFilterDialog(deps, "HSB/HSL", [
    { key: "hue", label: "Hue", type: "slider", default: 0, min: -180, max: 180 },
    { key: "saturation", label: "Saturation", type: "slider", default: 0, min: -100, max: 100 },
    { key: "lightness", label: "Lightness", type: "slider", default: 0, min: -100, max: 100 },
  ], (_r) => {
    // HSB/HSL is an alias for Hue/Saturation in simplified form
    applyFilter(deps, UtilitySuite, { mode: "high-pass", radius: 0.1, offsetX: 0, offsetY: 0, fill: "wrap", bgColor: { r: 0, g: 0, b: 0 }, seed: 0 });
    deps.showTip("HSB/HSL: Use Hue/Saturation (Ctrl+U) for full control.");
  });
}

// ---------------------------------------------------------------------------
// Repeat last filter
// ---------------------------------------------------------------------------

export function repeatLastFilter(deps: FilterDialogDeps): void {
  if (!deps.lastFilter) return;
  applyFilter(deps, deps.lastFilter.filter, deps.lastFilter.params);
}
